import React from 'react';
import classnames from 'classnames';
import {useCompleteInspectionContext} from '../../CompleteInspectionContext';
import * as _ from 'lodash';
import Media from '../../Media';
import {findFileByTypeId, getMediaType} from '../../../utils/files';
import {fileTypeId} from '../../../../Inspection/constants/fileTypeId';
import {getFullDataURI} from '../../../utils/getFullDataURI';
import {checkIfTextEndsWithDot, getBreakLinesTextShumaType3} from '../../../utils/texts';
import {formattingDate} from '../../../utils/formatting';
import {getSelectedOptionLabel} from '../../../utils/options';
import styles from './styles.module.scss';

export const exceedUsePermitText = 'על פי הממצאים קיים היתר לשימוש חורג';
export const exceedBuildingText = 'על פי הממצאים קיימות חריגות בנייה';

const HeadLine1 = React.forwardRef(({}, ref) => {
  return (
    <div ref={ref}>
      <HeadContent/>
    </div>
  );
});

const HeadContent = () => {
  const {
    data: {
      pdfData: {
        validLocalPlansDetails,
        countryStateData,
        plansInProgress,
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
        housePermitMismatchText,
        form4,
        form4Text,
        completeCert,
        dangerousStructure
      },
      files
    },
    sectionNumbersShumaType3,
  } = useCompleteInspectionContext();
  const showHeader10 = _.some([
    findFileByTypeId(files, fileTypeId.PLANS_IN_EFFECT),
    validLocalPlansDetails,
    countryStateData,
    plansInProgress,
    buildPermitNumber,
    buildPermitDate,
    buildPermitDetails,
    additionalBuildPermitText,
    supervisionFile,
    exceedUsePermit,
    exceedBuilding,
    housePermitMismatchText,
    form4,
    form4Text,
    completeCert,
    dangerousStructure,
    findFileByTypeId(files, fileTypeId.PERMIT_BLUEPRINT)
  ]);

  return showHeader10 && (
    <div className={classnames(styles.textRegular, styles.sectionContainer)}>
          <span className={classnames(styles.textUnderline, styles.bold)}>
            <span className={styles.sectionNumber}>{`${sectionNumbersShumaType3.section10}. `}</span>
            <span>מצב תכנוני</span>
          </span>
    </div>
  );
};

const Head = React.forwardRef(({}, ref) => {
  return (
    <div ref={ref}>
      <HeadContent/>
    </div>
  );
});

const Line1Image = React.forwardRef(({}, ref) => {
  const context = useCompleteInspectionContext();
  const file = findFileByTypeId(context.files, fileTypeId.PLANS_IN_EFFECT);
  const mediaType = file && getMediaType(file);
  const imageSrc = file && getFullDataURI(file);

  return (
    <div ref={ref}>
      {file && (
        <div className={classnames(styles.sectionContainer)}>
          <div
            className={classnames(
              styles.textRegular,
              styles.innerPadding,
              styles.bold
            )}
          >
            קובץ תכניות בתוקף:
          </div>
          <div className={styles.mediaContainer}>
            <Media
              src={imageSrc}
              file={file}
              alt="file"
              className={styles.mb3}
              type={mediaType}
            />
          </div>
        </div>
      )}
    </div>
  );
});

const Line1a = React.forwardRef(({}, ref) => {
  const {
    data: {
      pdfData: {
        landHafkaaText
      },
    },
  } = useCompleteInspectionContext();

  return Boolean(landHafkaaText) ? (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)} ref={ref}>
      <span className={classnames(styles.bold)}>הפקעה: </span>
      <span>{getBreakLinesTextShumaType3(landHafkaaText)}</span>
    </div>
  ) : <div ref={ref}/>;
});

const Line2a = React.forwardRef(({}, ref) => {
  const {
    data: {
      pdfData: {
        landHazmadaText
      },
    },
  } = useCompleteInspectionContext();

  return Boolean(landHazmadaText) ? (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)} ref={ref}>
      <span className={classnames(styles.bold)}>הצמדה של קרקע: </span>
      <span>{getBreakLinesTextShumaType3(landHazmadaText)}</span>
    </div>
  ) : <div ref={ref}/>;
});

const Line3a = React.forwardRef(({}, ref) => {
  const {
    data: {
      pdfData: {
        landZikatHanaaText
      },
    },
  } = useCompleteInspectionContext();

  return Boolean(landZikatHanaaText) ? (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)} ref={ref}>
      <span className={classnames(styles.bold)}>זיקת הנאה: </span>
      <span>{getBreakLinesTextShumaType3(landZikatHanaaText)}</span>
    </div>
  ) : <div ref={ref}/>;
});

const Line4a = React.forwardRef(({}, ref) => {
  const {
    data: {
      pdfData: {
        landOtherText
      },
    },
  } = useCompleteInspectionContext();

  return Boolean(landOtherText) ? (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)} ref={ref}>
      <span>{getBreakLinesTextShumaType3(landOtherText)}</span>
    </div>
  ) : <div ref={ref}/>;
});

const SubHeaderLine2Content = () => {
  const {
    data: {
      pdfData: {
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
        housePermitMismatchText,
        form4,
        form4Text,
        completeCert,
      },
    },
  } = useCompleteInspectionContext();

  return (
    <>
      <SubHead1Content/>
      <Line2Content/>
      {!_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
      ]) && <Line3Content/>}
      {!_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
      ]) && <Line4Content/>}
      {!_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
      ]) && <Line5Content/>}
      {!_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
      ]) && <Line6Content/>}
      {!_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
      ]) && <Line7Content/>}
      {!_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
        housePermitMismatchText,
      ]) && <Line8Content/>}
      {!_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
        housePermitMismatchText,
        form4,
        form4Text,
      ]) && <Line9Content/>}
      {!_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
        housePermitMismatchText,
        form4,
        form4Text,
        completeCert,
      ]) && <Line10Content/>}
    </>
  );
};

const SubHeaderLine2 = React.forwardRef(({}, ref) => {
  return (
    <div ref={ref}>
      <SubHeaderLine2Content/>
    </div>
  );
});

const SubHead1Content = () => {
  const {
    data: {
      pdfData: {
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
        housePermitMismatchText,
        form4,
        form4Text,
        completeCert,
        dangerousStructure
      }
    },
  } = useCompleteInspectionContext();
  const showSubHeaderCondition =
    _.some([
      buildPermitNumber,
      buildPermitDate,
      buildPermitDetails,
      additionalBuildPermitText,
      supervisionFile,
      exceedUsePermit,
      exceedBuilding,
      housePermitMismatchText,
      form4,
      form4Text,
      completeCert,
      dangerousStructure
    ]);

  return showSubHeaderCondition && (
    <div
      className={classnames(
        styles.textRegular,
        styles.sectionContainer,
        styles.innerPadding,
        styles.textUnderline,
        styles.bold
      )}
      id={'SubHead1Content'}
    >
      רישוי
    </div>
  );
};

const Line2Content = () => {
  const {
    data: {
      pdfData: {
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
      },
    },
  } = useCompleteInspectionContext();

  const text = [
    Boolean(buildPermitNumber) ? 'מס\' היתר: ' : '',
    Boolean(buildPermitNumber) ? `${buildPermitNumber}, ` : '',
    Boolean(buildPermitDate) ? 'מתאריך: ' : '',
    Boolean(buildPermitDate) ? `${formattingDate(buildPermitDate)}, ` : '',
    Boolean(buildPermitDetails) ? 'מהות היתר: ' : '',
    ...getBreakLinesTextShumaType3(buildPermitDetails)
  ];

  return _.some([buildPermitNumber, buildPermitDate, buildPermitDetails]) && (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)}>
      <span className={classnames(styles.bold)}>היתרי בנייה מקורי - </span>
      <span>{text}</span>
    </div>
  );
};

const Line3Content = () => {
  const {
    data: {
      pdfData: {
        additionalBuildPermitText
      },
    },
  } = useCompleteInspectionContext();

  return Boolean(additionalBuildPermitText) && (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)}>
      <span>{getBreakLinesTextShumaType3(additionalBuildPermitText)}</span>
    </div>
  );
};

const Line3 = React.forwardRef(({}, ref) => {
  const {
    data: {
      pdfData: {
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
      },
    },
  } = useCompleteInspectionContext();

  return (
    <div ref={ref}>
      {_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
      ]) && <Line3Content/>}
    </div>
  );
});

const Line4Content = () => {
  const {
    data: {
      pdfData: {
        supervisionFile
      },
    },
    options
  } = useCompleteInspectionContext();
  const supervisionFileValue = getSelectedOptionLabel(supervisionFile, options.supervisionFileOptions);

  return Boolean(supervisionFile) && (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)}>
      <span className={classnames(styles.bold)}>תיק פיקוח - </span>
      <span>{supervisionFileValue}{checkIfTextEndsWithDot(supervisionFileValue)}</span>
    </div>
  );
};

const Line4 = React.forwardRef(({}, ref) => {
  const {
    data: {
      pdfData: {
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
      },
    },
  } = useCompleteInspectionContext();

  return (
    <div ref={ref}>
      {_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
      ]) && <Line4Content/>}
    </div>
  );
});

const Line5Content = () => {
  const {
    data: {
      pdfData: {
        exceedUsePermit
      },
    },
    options
  } = useCompleteInspectionContext();
  const exceedUsePermitValue = exceedUsePermit === 1
    ? exceedUsePermitText
    : getSelectedOptionLabel(exceedUsePermit, options.exceedUsePermitOptions);

  return Boolean(exceedUsePermit) && (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)}>
      <span className={classnames(styles.bold)}>היתר לשימוש חורג - </span>
      <span>{exceedUsePermitValue}{checkIfTextEndsWithDot(exceedUsePermitValue)}</span>
    </div>
  );
};

const Line5 = React.forwardRef(({}, ref) => {
  const {
    data: {
      pdfData: {
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
      },
    },
  } = useCompleteInspectionContext();

  return (
    <div ref={ref}>
      {_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
      ]) && <Line5Content/>}
    </div>
  );
});

const Line6Content = () => {
  const {
    data: {
      pdfData: {
        exceedBuilding
      },
    },
    options
  } = useCompleteInspectionContext();
  const exceedBuildingValue = exceedBuilding === 1
    ? exceedBuildingText
    : getSelectedOptionLabel(exceedBuilding, options.exceedBuildingOptions);

  return Boolean(exceedBuilding) && (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)}>
      <span className={classnames(styles.bold)}>חריגות בנייה - </span>
      <span>{exceedBuildingValue}{checkIfTextEndsWithDot(exceedBuildingValue)}</span>
    </div>
  );
};

const Line6 = React.forwardRef(({}, ref) => {
  const {
    data: {
      pdfData: {
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
      },
    },
  } = useCompleteInspectionContext();

  return (
    <div ref={ref}>
      {_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
      ]) && <Line6Content/>}
    </div>
  );
});

const Line7Content = () => {
  const {
    data: {
      pdfData: {
        housePermitMismatchText
      },
    },
  } = useCompleteInspectionContext();

  return Boolean(housePermitMismatchText) && (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)}>
      <span className={classnames(styles.bold)}>הערות כלליות - </span>
      <span>{getBreakLinesTextShumaType3(housePermitMismatchText)}</span>
    </div>
  );
};

const Line7 = React.forwardRef(({}, ref) => {
  const {
    data: {
      pdfData: {
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
      },
    },
  } = useCompleteInspectionContext();

  return (
    <div ref={ref}>
      {_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
      ]) && <Line7Content/>}
    </div>
  );
});

const Line8Content = () => {
  const {
    data: {
      pdfData: {
        form4,
        form4Text
      },
    },
    options
  } = useCompleteInspectionContext();
  const form4Value = getSelectedOptionLabel(form4, options.form4Options);
  const text = [
    Boolean(form4) ? `${form4Value}` : '',
    Boolean(form4Text) ? `, ${form4Text}${checkIfTextEndsWithDot(form4Text)}` : '',
  ];

  return _.some([form4, form4Text]) && (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)}>
      <span className={classnames(styles.bold)}>טופס 4: </span>
      <span>{text}</span>
      <span>{Boolean(form4Text) ? '' : '.'}</span>
    </div>
  );
};

const Line8 = React.forwardRef(({}, ref) => {
  const {
    data: {
      pdfData: {
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
        housePermitMismatchText,
      },
    },
  } = useCompleteInspectionContext();

  return (
    <div ref={ref}>
      {_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
        housePermitMismatchText,
      ]) && <Line8Content/>}
    </div>
  );
});

const Line9Content = () => {
  const {
    data: {
      pdfData: {
        completeCert,
        completeCertText
      },
    },
    options
  } = useCompleteInspectionContext();
  const completeCertValue = getSelectedOptionLabel(completeCert, options.completeCertOptions);
  const text = [
    Boolean(completeCert) ? `${completeCertValue}` : '',
    Boolean(completeCertText) && completeCert !== 2 ? ', ' : '.',
    Boolean(completeCertText) && completeCert !== 2 ? getBreakLinesTextShumaType3(completeCertText) : '',
  ];

  return Boolean(completeCert) && (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)}>
      <span className={classnames(styles.bold)}>תעודת גמר: </span>
      <span>{text}</span>
    </div>
  );
};

const Line9 = React.forwardRef(({}, ref) => {
  const {
    data: {
      pdfData: {
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
        housePermitMismatchText,
        form4,
        form4Text,
      },
    },
  } = useCompleteInspectionContext();

  return (
    <div ref={ref}>
      {_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
        housePermitMismatchText,
        form4,
        form4Text,
      ]) && <Line9Content/>}
    </div>
  );
});

const Line10Content = () => {
  const {
    data: {
      pdfData: {
        dangerousStructure,
        dangerousStructureText
      },
    },
    options
  } = useCompleteInspectionContext();
  const dangerousStructureValue = getSelectedOptionLabel(dangerousStructure, options.dangerousStructureOptions);
  const text = [
    Boolean(dangerousStructure) ? `${dangerousStructureValue}` : '',
    Boolean(dangerousStructureText) && dangerousStructure === 1 ? ', ' : '.',
    Boolean(dangerousStructureText) && dangerousStructure === 1 ? getBreakLinesTextShumaType3(dangerousStructureText) : '',
  ];

  return Boolean(dangerousStructure) && (
    <div className={classnames(styles.textRegular, styles.sectionContainer, styles.innerPadding)}>
      <span className={classnames(styles.bold)}>מבנה מסוכן: </span>
      <span>{text}</span>
    </div>
  );
};

const Line10 = React.forwardRef(({}, ref) => {
  const {
    data: {
      pdfData: {
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
        housePermitMismatchText,
        form4,
        form4Text,
        completeCert,
      },
    },
  } = useCompleteInspectionContext();

  return (
    <div ref={ref}>
      {_.some([
        buildPermitNumber,
        buildPermitDate,
        buildPermitDetails,
        additionalBuildPermitText,
        supervisionFile,
        exceedUsePermit,
        exceedBuilding,
        housePermitMismatchText,
        form4,
        form4Text,
        completeCert,
      ]) && <Line10Content/>}
    </div>
  );
});

export default {
  HeadLine1,
  Head,
  SubHeaderLine2,
  Line1Image,
  Line1a,
  Line2a,
  Line3a,
  Line4a,
  Line3,
  Line4,
  Line5,
  Line6,
  Line7,
  Line8,
  Line9,
  Line10,
};